import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Book as BookIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import { usePhase } from '../../../contexts/PhaseContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const SubjectReportsPage = () => {
  const { t } = useTranslation();
  const { activePhase } = usePhase();
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diag, setDiag] = useState({ urls: {}, headers: {}, counts: {} });

  const resolvedPhaseCode = useMemo(() => {
    const fromCtx = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
    return fromCtx ? String(fromCtx).trim().toLowerCase() : '';
  }, [activePhase]);
  const isSecPhase = resolvedPhaseCode === 'sec' || resolvedPhaseCode === 'secondary';
  const resolvedPhaseId = useMemo(() => {
    const raw = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
    const parsed = parseInt(String(raw), 10);
    if (Number.isInteger(parsed)) return parsed;
    if (isSecPhase) return 2;
    if (resolvedPhaseCode === 'nur_pri' || resolvedPhaseCode === 'nursery' || resolvedPhaseCode === 'nursery_primary') return 1;
    return null;
  }, [resolvedPhaseCode, isSecPhase]);
  const filterByPhaseContext = useMemo(() => {
    const nurPriLevels = ['nursery', 'primary'];
    const secLevels = ['first_cycle', 'second_cycle'];
    const forbiddenLevels = resolvedPhaseCode === 'nur_pri' ? secLevels
      : resolvedPhaseCode === 'sec' ? nurPriLevels : [];
    return (items = []) => {
      if (!Array.isArray(items)) return [];
      if (!resolvedPhaseId) return items;
      return items.filter((item) => {
        const lvl = (item?.level || '').toString().trim().toLowerCase();
        if (lvl && forbiddenLevels.includes(lvl)) return false;
        const candidateId = Number(item?.phaseId ?? item?.PhaseId ?? item?.phase_id);
        if (Number.isInteger(candidateId)) return candidateId === resolvedPhaseId;
        const candidateCode = (item?.phaseCode ?? item?.Phase?.code ?? item?.phase_code ?? '').toString().toLowerCase();
        if (candidateCode) return candidateCode === resolvedPhaseCode;
        return false;
      });
    };
  }, [resolvedPhaseCode, resolvedPhaseId]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  
  // Statistics
  const [stats, setStats] = useState({
    totalSubjects: 0,
    activeSubjects: 0,
    inactiveSubjects: 0,
    subjectsWithHeads: 0,
    subjectsAssignedToClasses: 0
  });

  useEffect(() => {
    fetchInitialData();
  }, [resolvedPhaseCode]);

  // Helper: timeout wrapper to prevent infinite waits
  const requestWithTimeout = async (path, options = {}, ms = 15000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      return await fetchJson(path, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Context + cache keys
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
      const kSubjects = `subjReports:subjects:${ctxKey}`;
      const kClasses = `subjReports:classes:${ctxKey}`;
      const kTeachers = `subjReports:teachers:${ctxKey}`;
      const kAllocMap = `subjReports:allocMap:${ctxKey}`;
      const kCategories = `subjReports:categories:${ctxKey}`;
      let prefilled = false;
      try { const r = localStorage.getItem(kSubjects); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setSubjects(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const r = localStorage.getItem(kClasses); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setClasses(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const r = localStorage.getItem(kTeachers); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setTeachers(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const r = localStorage.getItem(kCategories); if (r) { const v = JSON.parse(r); if (Array.isArray(v) && v.length) setCategories(v); } } catch(_) {}
      let cachedAllocMap = null;
      try { const r = localStorage.getItem(kAllocMap); if (r) { const v = JSON.parse(r); if (v && typeof v === 'object') { cachedAllocMap = v; } } } catch(_) {}
      if (prefilled) setLoading(false);
      let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}

      // Build headers and query tail like SubjectManagementPage
      const headers = {};
      if (schoolId) headers['X-Scope-School-Id'] = schoolId;
      if (phaseCode) headers['X-Active-Phase'] = phaseCode;
      if (ayId) headers['X-Academic-Year-Id'] = ayId;
      if (phaseId) headers['X-Active-Phase-Id'] = phaseId;
      const qsTail = [
        phaseCode ? `phase=${encodeURIComponent(phaseCode)}` : '',
        schoolId ? `schoolId=${encodeURIComponent(schoolId)}` : ''
      ].filter(Boolean).join('&');

      const urls = {
        subjectsAlloc: '/api/allocations/subjects',
        classesAlloc: '/api/allocations/classes',
        classesMain: `/api/classes${qsTail ? `?${qsTail}` : ''}`,
        classesLite: (() => {
          const params = [];
          if (phaseCode) params.push(`phase=${encodeURIComponent(phaseCode)}`);
          if (schoolId) params.push(`schoolId=${encodeURIComponent(schoolId)}`);
          return `/api/classes-lite${params.length ? `?${params.join('&')}` : ''}`;
        })(),
        users: `/api/users${qsTail ? `?${qsTail}` : ''}`,
        allocNames: (() => {
          const params = [];
          if (phaseCode) params.push(`phase=${encodeURIComponent(phaseCode)}`);
          if (phaseId) params.push(`phaseId=${encodeURIComponent(phaseId)}`);
          if (schoolId) params.push(`schoolId=${encodeURIComponent(schoolId)}`);
          if (ayId) params.push(`academicYearId=${encodeURIComponent(ayId)}`);
          return `/api/allocations/class-subjects/names${params.length ? `?${params.join('&')}` : ''}`;
        })(),
        subjectsMain: `/api/subjects?include=category${qsTail ? `&${qsTail}` : ''}`,
        subjectsAllPhases: '/api/subjects?include=category&allPhases=1',
        departments: '/api/departments',
        subjectGroups: '/api/subject-groups?includeVariants=0',
      };
      try { console.debug('[SubjectReportsPage] headers', headers, 'urls', urls); } catch(_) {}

      const [subjectsData, classesAllocResp, usersResp, allocNames, departmentsResp, subjectGroupsResp] = await Promise.all([
        requestWithTimeout(urls.subjectsAlloc, { headers }, 15000),
        requestWithTimeout(urls.classesAlloc, { headers }, 12000),
        requestWithTimeout(urls.users, { headers }, 15000),
        requestWithTimeout(urls.allocNames, { headers }, 15000),
        isSecPhase ? requestWithTimeout(urls.departments, { headers }, 15000).catch(() => []) : Promise.resolve([]),
        isSecPhase ? requestWithTimeout(urls.subjectGroups, { headers }, 15000).catch(() => []) : Promise.resolve([])
      ]);

      // Handle different response structures
      // Subjects normalization + fallbacks (mirror SubjectManagementPage)
      let subjectsArray = Array.isArray(subjectsData) ? subjectsData : 
                          (subjectsData?.rows || subjectsData?.subjects || subjectsData?.data || []);
      // Fallbacks: try main subjects endpoint with include=category, then allPhases=1
      if (!Array.isArray(subjectsArray) || subjectsArray.length === 0) {
        try {
          const alt = await requestWithTimeout(urls.subjectsMain, { headers }, 15000);
          subjectsArray = Array.isArray(alt) ? alt : (alt?.subjects || alt?.rows || alt?.data || []);
        } catch (_) {}
      }
      if ((!Array.isArray(subjectsArray) || subjectsArray.length === 0) && !phaseCode && !phaseId) {
        try {
          const alt2 = await requestWithTimeout(urls.subjectsAllPhases, { headers }, 15000);
          subjectsArray = Array.isArray(alt2) ? alt2 : (alt2?.subjects || alt2?.rows || alt2?.data || []);
        } catch (_) {}
      }
      if ((!Array.isArray(subjectsArray) || subjectsArray.length === 0) && !phaseCode && !phaseId) {
        try {
          const alt3 = await requestWithTimeout('/api/allocations/subjects?allPhases=1', { headers }, 15000);
          subjectsArray = Array.isArray(alt3) ? alt3 : (alt3?.subjects || alt3?.data || alt3 || []);
        } catch (_) {}
      }
      // Classes: prefer allocations/classes (fast), then /api/classes, then classes-lite
      let classesArray = Array.isArray(classesAllocResp) ? classesAllocResp : 
                         (classesAllocResp?.classes || classesAllocResp?.data || []);
      if (!Array.isArray(classesArray) || classesArray.length === 0) {
        try {
          const cls = await requestWithTimeout(urls.classesMain, { headers }, 12000);
          classesArray = Array.isArray(cls) ? cls : (cls?.classes || cls?.data || []);
        } catch (_) {}
      }
      if (!Array.isArray(classesArray) || classesArray.length === 0) {
        try {
          const lite = await requestWithTimeout(urls.classesLite, { headers }, 12000);
          classesArray = Array.isArray(lite) ? lite : (lite?.classes || lite?.data || []);
        } catch (_) {}
      }
      // Teachers: normalize and filter to role Teacher (name or roleId 3)
      let usersArray = Array.isArray(usersResp?.rows) ? usersResp.rows : (Array.isArray(usersResp) ? usersResp : (usersResp?.users || usersResp?.data || []));
      const teachersArray = Array.isArray(usersArray) ? usersArray.filter(u => {
        const roleName = (u.role || u.roleName || '').toString().toLowerCase();
        const roleId = Number(u.roleId || u.RoleId || u.role_id || 0);
        return roleName === 'teacher' || roleId === 3;
      }) : [];
      const departmentsArrayRaw = Array.isArray(departmentsResp) ? departmentsResp : (departmentsResp?.rows || departmentsResp?.data || []);
      const subjectGroupsArrayRaw = Array.isArray(subjectGroupsResp) ? subjectGroupsResp : (subjectGroupsResp?.rows || subjectGroupsResp?.data || []);
      const departmentsArray = filterByPhaseContext(departmentsArrayRaw);
      const subjectGroupsArray = filterByPhaseContext(subjectGroupsArrayRaw);
      const departmentById = new Map((departmentsArray || []).map((department) => [String(department.id), department]));
      const subjectGroupById = new Map((subjectGroupsArray || []).map((group) => [String(group.id), group]));
      
      // Build SubjectId => classes[] mapping using allocations names map
      const allocMap = (allocNames && allocNames.map) ? allocNames.map : (cachedAllocMap || {});
      const classLookup = {};
      for (const c of classesArray) { classLookup[String(c.id)] = c; }
      const subjectToClasses = {};
      try {
        Object.entries(allocMap).forEach(([classId, subjectList]) => {
          const cls = classLookup[String(classId)];
          if (!cls || !Array.isArray(subjectList)) return;
          for (const s of subjectList) {
            const sid = String(s.id || s.subjectId || '');
            if (!sid) continue;
            if (!subjectToClasses[sid]) subjectToClasses[sid] = [];
            subjectToClasses[sid].push({ id: cls.id, name: cls.name, section: cls.section || null });
          }
        });
      } catch (_) {}

      // Enhance subjects with classes[] for reporting
      // Apply strict phase + level filtering
      subjectsArray = filterByPhaseContext(subjectsArray);

      const enhancedSubjects = (Array.isArray(subjectsArray) ? subjectsArray : []).map(s => {
        const sid = String(s.id || '');
        const classesForSubj = subjectToClasses[sid] || [];
        // normalize common flags/fields for reporting
        const active = (typeof s.active === 'boolean') ? s.active
                      : (typeof s.isActive === 'boolean') ? s.isActive
                      : true; // default to active when unspecified
        const code = s.code || s.shortCode || s.abbr || '';
        const category = s.category || s.categoryName || null;
        const groupId = s.subjectGroupId || s.group?.id || null;
        const subjectGroup = groupId ? subjectGroupById.get(String(groupId)) : null;
        const departmentId = subjectGroup?.departmentId || null;
        const department = departmentId ? departmentById.get(String(departmentId)) : null;
        const level = (s.level ?? s.classLevel ?? s.Level ?? s.levelName ?? null);
        return {
          ...s,
          active,
          code,
          category,
          level,
          classes: classesForSubj,
          departmentId: department?.id || departmentId || null,
          departmentName: department?.name || null,
          departmentHeadId: department?.subjectHeadId || null,
          departmentHeadType: department?.subjectHeadType || null,
        };
      });

      // Fetch subject categories scoped to this school/phase
      let categoriesArray = [];
      try {
        const catQs = [phaseCode ? `phase=${encodeURIComponent(phaseCode)}` : '', schoolId ? `schoolId=${encodeURIComponent(schoolId)}` : ''].filter(Boolean).join('&');
        const catData = await requestWithTimeout(`/api/subject-categories${catQs ? `?${catQs}` : ''}`, { headers }, 10000);
        categoriesArray = Array.isArray(catData) ? catData : (catData?.categories || catData?.data || []);
      } catch (_) {}

      setSubjects(enhancedSubjects);
      setClasses(classesArray);
      setTeachers(teachersArray);
      setAllUsers(usersArray);
      setCategories(categoriesArray);
      try { localStorage.setItem(kSubjects, JSON.stringify(enhancedSubjects)); } catch(_) {}
      try { localStorage.setItem(kClasses, JSON.stringify(classesArray)); } catch(_) {}
      try { localStorage.setItem(kTeachers, JSON.stringify(teachersArray)); } catch(_) {}
      try { localStorage.setItem(kAllocMap, JSON.stringify(allocMap)); } catch(_) {}
      try { localStorage.setItem(kCategories, JSON.stringify(categoriesArray)); } catch(_) {}
      
      calculateStats(enhancedSubjects);
      try {
        setDiag({
          urls,
          headers,
          counts: {
            subjects: enhancedSubjects.length,
            classes: classesArray.length,
            teachers: teachersArray.length
          }
        });
      } catch(_) {}
      setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(t('subjectReports.errors.loadData'));
      setLoading(false);
    }
  };

  useScopeAutoRefresh([fetchInitialData]);

  const calculateStats = (subjectsData) => {
    const arr = Array.isArray(subjectsData) ? subjectsData : [];
    const total = arr.length;
    const active = arr.filter(s => (typeof s.active === 'boolean') ? s.active : true).length;
    const inactive = total - active;
    const withHeads = isSecPhase ? arr.filter(s => !!s.departmentHeadId).length : 0;
    const assignedToClasses = arr.filter(s => Array.isArray(s.classes) && s.classes.length > 0).length;

    setStats({
      totalSubjects: total,
      activeSubjects: active,
      inactiveSubjects: inactive,
      subjectsWithHeads: withHeads,
      subjectsAssignedToClasses: assignedToClasses
    });
  };

  const getFilteredSubjects = () => {
    return subjects.filter(subject => {
      const matchesSearch = (subject.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (subject.code || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'active' ? subject.active : !subject.active);
      const matchesCategory = categoryFilter === 'all' || 
                            (isSecPhase
                              ? subject.departmentName === categoryFilter
                              : (subject.category && typeof subject.category === 'object'
                                  ? subject.category.name === categoryFilter
                                  : subject.category === categoryFilter));
      const matchesClass = classFilter === 'all' || 
                          (subject.classes && subject.classes.some(c => c.id === parseInt(classFilter)));

      const subjLevel = (subject?.level || '').toString().trim().toLowerCase();
      const matchesLevel = levelFilter === 'all' || !subjLevel || subjLevel === String(levelFilter).toLowerCase();

      return matchesSearch && matchesStatus && matchesCategory && matchesClass && matchesLevel;
    });
  };

  const getDepartmentHeadName = (subject) => {
    const subjectHeadId = subject?.departmentHeadId;
    const subjectHeadType = (subject?.departmentHeadType || '').toString().trim().toLowerCase();
    if (!subjectHeadId) return t('subjectReports.labels.notAssigned');
    const clean = (v) => {
      const s = (v ?? '').toString().trim();
      if (!s) return '';
      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null' || low === 'nan') return '';
      return s;
    };
    if (subjectHeadType === 'staff') return t('subjectAssignments.labels.staffWithId', { id: subjectHeadId });
    const tid = String(subjectHeadId);
    const pool = Array.isArray(allUsers) && allUsers.length ? allUsers : (Array.isArray(teachers) ? teachers : []);
    const u = pool.find(t => String(t.id) === tid);
    if (!u) return t('subjectReports.labels.userWithId', { id: tid });
    const displayName =
      clean(u.displayName || u.preferredName) ||
      clean(u.fullName || u.full_name) ||
      (() => {
        const first = clean(u.firstName || u.first_name);
        const last  = clean(u.lastName || u.last_name);
        const combined = [first, last].filter(Boolean).join(' ').trim();
        return combined;
      })() ||
      clean(u.name) ||
      clean(u.username) ||
      clean(u.email) ||
      t('subjectReports.labels.userWithId', { id: tid });
    return displayName;
  };

  const getDepartmentName = (subject) => {
    if (!isSecPhase) return getCategoryName(subject);
    return subject?.departmentName || null;
  };

  const getAssignedClasses = (subject) => {
    if (!subject.classes || subject.classes.length === 0) return t('subjectReports.labels.none');
    return subject.classes.map(c => `${c.name}${c.section ? `-${c.section}` : ''}`).join(', ');
  };

  const getStatusColor = (active) => {
    return active ? 'success' : 'error';
  };

  const getStatusText = (active) => {
    return active ? t('status.active') : t('status.inactive');
  };

  const handleExport = async (format) => {
    try {
      const filteredData = getFilteredSubjects();
      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        setError(t('subjectReports.errors.exportData'));
        return;
      }
      const exportHeaders = {
        subjectName: t('subjectReports.export.headers.subjectName'),
        subjectCode: t('subjectReports.export.headers.subjectCode'),
        level: t('subjectManagement.add.fields.level', { defaultValue: 'Level' }),
        category: isSecPhase ? t('common.department', { defaultValue: 'Department' }) : t('subjectReports.export.headers.category'),
        description: t('subjectReports.export.headers.description'),
        status: t('subjectReports.export.headers.status'),
        assignedClasses: t('subjectReports.export.headers.assignedClasses'),
        createdDate: t('subjectReports.export.headers.createdDate')
      };
      if (isSecPhase) {
        exportHeaders.subjectHead = 'H.O.D';
      }
      const exportData = filteredData.map(subject => ({
        [exportHeaders.subjectName]: subject.name,
        [exportHeaders.subjectCode]: subject.code,
        [exportHeaders.level]: subject.level || '',
        [exportHeaders.category]: getDepartmentName(subject) || '',
        [exportHeaders.description]: subject.description || '',
        [exportHeaders.status]: getStatusText(subject.active),
        [exportHeaders.assignedClasses]: getAssignedClasses(subject),
        [exportHeaders.createdDate]: new Date(subject.createdAt).toLocaleDateString()
      }));

      if (isSecPhase) {
        for (let i = 0; i < exportData.length; i += 1) {
          exportData[i][exportHeaders.subjectHead] = getDepartmentHeadName(filteredData[i]);
        }
      }

      if (format === 'csv') {
        const csvContent = [
          Object.keys(exportData[0]).join(','),
          ...exportData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `subject-reports-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(t('subjectReports.errors.exportData'));
    }
  };

  const getCategoryName = (subject) => {
    if (subject.category && typeof subject.category === 'object') return subject.category.name;
    if (subject.category) return subject.category;
    if (subject.categoryId && categories.length) {
      const cat = categories.find(c => String(c.id) === String(subject.categoryId));
      if (cat) return cat.name;
    }
    return null;
  };

  // Only block when nothing cached yet
  if (loading && subjects.length === 0 && classes.length === 0 && teachers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader variant="circular3" size={56} centered minHeight="40vh" messages={[]} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const filteredSubjects = getFilteredSubjects();
  const secondaryDepartmentNames = isSecPhase
    ? [...new Set((subjects || []).map((subject) => subject?.departmentName).filter(Boolean))].sort()
    : [];

  return (
    <Box>
      {/* Empty state notice when nothing is loaded after fetch */}
      {(!loading && subjects.length === 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('subjectReports.emptyState.noSubjectsForContext')}
        </Alert>
      )}
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('subjectReports.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('subjectReports.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
            sx={{ mr: 1 }}
          >
            {t('datagrid.exportCSV')}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SchoolIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {stats.totalSubjects}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subjectReports.stats.totalSubjects')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <BookIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {stats.activeSubjects}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subjectReports.stats.activeSubjects')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <BookIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {stats.inactiveSubjects}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subjectReports.stats.inactiveSubjects')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {isSecPhase && (
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" component="div">
                  {stats.subjectsWithHeads}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('subjectReports.stats.withSubjectHeads')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {stats.subjectsAssignedToClasses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subjectReports.stats.assignedToClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('datagrid.filters')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label={t('subjectReports.filters.search.label')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('subjectReports.filters.search.placeholder')}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('subjectReports.filters.status.label')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('subjectReports.filters.status.label')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('subjectReports.filters.status.all')}</MenuItem>
                  <MenuItem value="active">{t('status.active')}</MenuItem>
                  <MenuItem value="inactive">{t('status.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{isSecPhase ? t('common.department', { defaultValue: 'Department' }) : t('subjectReports.filters.category.label')}</InputLabel>
                <Select
                  value={categoryFilter}
                  label={isSecPhase ? t('common.department', { defaultValue: 'Department' }) : t('subjectReports.filters.category.label')}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('common.all', { defaultValue: 'All' })}</MenuItem>
                  {isSecPhase
                    ? secondaryDepartmentNames.map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))
                    : categories.length > 0
                    ? categories.map(cat => (
                        <MenuItem key={cat.id || cat.name} value={cat.name}>
                          {cat.name}
                        </MenuItem>
                      ))
                    : [...new Set(subjects.map(s => {
                        if (s.category && typeof s.category === 'object') return s.category.name;
                        return s.category;
                      }).filter(Boolean))].sort().map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('subjectReports.filters.class.label')}</InputLabel>
                <Select
                  value={classFilter}
                  label={t('subjectReports.filters.class.label')}
                  onChange={(e) => setClassFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('subjectReports.filters.class.all')}</MenuItem>
                  {classes.map(cls => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name}{cls.section ? `-${cls.section}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</InputLabel>
                <Select
                  value={levelFilter}
                  label={t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('common.all', { defaultValue: 'All' })}</MenuItem>
                  {(resolvedPhaseCode === 'nur_pri' || !resolvedPhaseCode) && <MenuItem value="nursery">{t('subjectManagement.add.levels.nursery', { defaultValue: 'Nursery' })}</MenuItem>}
                  {(resolvedPhaseCode === 'nur_pri' || !resolvedPhaseCode) && <MenuItem value="primary">{t('subjectManagement.add.levels.primary', { defaultValue: 'Primary' })}</MenuItem>}
                  {(resolvedPhaseCode === 'sec' || !resolvedPhaseCode) && <MenuItem value="first_cycle">{t('subjectManagement.add.levels.firstCycle', { defaultValue: 'First Cycle' })}</MenuItem>}
                  {(resolvedPhaseCode === 'sec' || !resolvedPhaseCode) && <MenuItem value="second_cycle">{t('subjectManagement.add.levels.secondCycle', { defaultValue: 'Second Cycle' })}</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {t('subjectReports.results.summary', { count: filteredSubjects.length })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subjectReports.results.helper')}
        </Typography>
      </Box>

      {/* Subjects Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('subjectReports.table.headers.subjectName')}</TableCell>
              <TableCell>{t('subjectReports.table.headers.code')}</TableCell>
              <TableCell>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</TableCell>
              <TableCell>{isSecPhase ? t('common.department', { defaultValue: 'Department' }) : t('subjectReports.table.headers.category')}</TableCell>
              <TableCell>{t('subjectReports.table.headers.status')}</TableCell>
              {isSecPhase && <TableCell>H.O.D</TableCell>}
              <TableCell>{t('subjectReports.table.headers.assignedClasses')}</TableCell>
              <TableCell>{t('subjectReports.table.headers.description')}</TableCell>
              <TableCell>{t('subjectReports.table.headers.createdDate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSecPhase ? 9 : 8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('subjectReports.table.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSubjects.map((subject) => (
                <TableRow key={subject.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {subject.name || t('common.unnamedSubject')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={subject.code || t('common.noCode')} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={subject.level || t('common.na', { defaultValue: 'N/A' })} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getDepartmentName(subject) || t('subjectReports.labels.na')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(subject.active)}
                      color={getStatusColor(subject.active)}
                      size="small"
                    />
                  </TableCell>
                  {isSecPhase && (
                    <TableCell>
                      <Typography variant="body2">
                        {getDepartmentHeadName(subject)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {getAssignedClasses(subject)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {subject.description || t('subjectReports.labels.noDescription')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(subject.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SubjectReportsPage;
